import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowLeft, LogOut, MailCheck, MailX } from "lucide-react";
import BrandMark from "@/components/BrandMark";
import { useLanguage } from "@/i18n";
import { Input } from "@/components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatApiErrorDetail(detail) {
  if (detail == null) return "";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  return String(detail);
}

export default function Admin() {
  const { t } = useLanguage();
  const [user, setUser] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [enquiries, setEnquiries] = useState([]);

  useEffect(() => {
    axios.get(`${API}/auth/me`, { withCredentials: true })
      .then((r) => setUser(r.data))
      .catch(() => setUser(false));
  }, []);

  useEffect(() => {
    if (user) {
      axios.get(`${API}/enquiries`, { withCredentials: true })
        .then((r) => setEnquiries(r.data))
        .catch(() => setEnquiries([]));
    }
  }, [user]);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
      setUser(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || t.admin.loginError);
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }).catch(() => {});
    setUser(false);
  };

  if (user === null) {
    return <div className="min-h-screen bg-[#FDFBF7]" data-testid="admin-loading" />;
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center px-6" data-testid="admin-login-page">
        <div className="w-full max-w-md">
          <Link to="/" data-testid="admin-back-link" className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-sage hover:text-[#C9A227] transition-colors mb-10">
            <ArrowLeft className="w-4 h-4" /> Naples &amp; Beyond Tours
          </Link>
          <div className="flex items-center gap-2.5 mb-3">
            <BrandMark className="w-11 h-8" />
            <h1 className="font-serif text-3xl font-medium tracking-tight text-ink">{t.admin.title}</h1>
          </div>
          <p className="text-sm text-ink/60 mb-8">{t.admin.subtitle}</p>
          <form data-testid="admin-login-form" onSubmit={login} className="space-y-6">
            <div>
              <label htmlFor="admin-email" className="block text-xs font-bold uppercase tracking-[0.18em] text-ink/60 mb-2">{t.admin.email}</label>
              <Input id="admin-email" data-testid="admin-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                className="rounded-none border-0 border-b border-ink/25 bg-transparent px-0 py-3 focus-visible:ring-0 focus-visible:border-[#C9A227]" />
            </div>
            <div>
              <label htmlFor="admin-password" className="block text-xs font-bold uppercase tracking-[0.18em] text-ink/60 mb-2">{t.admin.password}</label>
              <Input id="admin-password" data-testid="admin-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                className="rounded-none border-0 border-b border-ink/25 bg-transparent px-0 py-3 focus-visible:ring-0 focus-visible:border-[#C9A227]" />
            </div>
            {error && <p data-testid="admin-login-error" className="text-sm text-[#C9A227]">{error}</p>}
            <button data-testid="admin-login-submit" type="submit" disabled={loading}
              className="w-full rounded-full bg-azure px-8 py-4 text-xs font-bold uppercase tracking-[0.15em] text-[#FDFBF7] hover:bg-[#C9A227] hover:text-[#0F2C50] transition-colors duration-300 disabled:opacity-60">
              {loading ? t.admin.loggingIn : t.admin.login}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="admin-dashboard">
      <header className="border-b border-border/60 bg-[#FDFBF7]/85 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-[1200px] mx-auto px-6 h-20 flex items-center justify-between">
          <Link to="/" data-testid="admin-brand-link" className="flex items-center gap-2.5">
            <BrandMark className="w-11 h-8" />
            <span className="font-serif text-xl font-medium tracking-tight text-ink">Naples <span className="text-[#C9A227] text-[1.35em] leading-none font-light">&amp;</span> <span className="text-[#2E86AB]">Beyond Tours</span></span>
          </Link>
          <button data-testid="admin-logout-button" onClick={logout}
            className="inline-flex items-center gap-2 rounded-full border border-ink/15 px-5 py-2.5 text-xs font-bold uppercase tracking-[0.15em] text-ink hover:border-[#C9A227] hover:text-[#C9A227] transition-colors duration-300">
            <LogOut className="w-3.5 h-3.5" /> {t.admin.logout}
          </button>
        </div>
      </header>

      <main className="max-w-[1200px] mx-auto px-6 py-12">
        <h1 className="font-serif text-4xl font-light tracking-tight text-ink mb-2">{t.admin.dashboard}</h1>
        <p className="text-sm text-sage mb-10">{user.email}</p>

        {enquiries.length === 0 ? (
          <p data-testid="admin-empty-state" className="text-base text-ink/60 py-20 text-center">{t.admin.empty}</p>
        ) : (
          <div className="space-y-4">
            {enquiries.map((q) => (
              <div key={q.enquiry_id} data-testid={`enquiry-row-${q.enquiry_id}`} className="bg-white/60 border border-border/60 rounded-sm p-6 lg:p-8">
                <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
                  <div>
                    <h2 className="font-serif text-2xl font-medium tracking-tight text-ink">{q.tour}</h2>
                    <p className="text-sm text-ink/70 mt-1">
                      <strong>{q.name}</strong> — <a href={`mailto:${q.email}`} className="text-azure hover:text-[#C9A227] transition-colors">{q.email}</a>
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <span className="text-xs text-sage">{new Date(q.created_at).toLocaleString("it-IT")}</span>
                    <span className={`inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.15em] ${q.email_sent ? "text-olive" : "text-[#C9A227]"}`}>
                      {q.email_sent ? <MailCheck className="w-3.5 h-3.5" /> : <MailX className="w-3.5 h-3.5" />}
                      {q.email_sent ? t.admin.mailSent : t.admin.mailPending}
                    </span>
                  </div>
                </div>
                <div className="flex flex-wrap gap-6 text-sm text-ink/70 mb-3">
                  {q.guests && <span><strong className="text-ink/85">{t.enquiry.guests}:</strong> {q.guests}</span>}
                  {q.date && <span><strong className="text-ink/85">{t.enquiry.date}:</strong> {q.date}</span>}
                </div>
                {q.message && <p className="text-sm leading-relaxed text-ink/75 border-t border-border/50 pt-4">{q.message}</p>}
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
